import React from 'react';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const ProgressBar = ({ value, color = 'bg-accent' }) => (
  <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
    <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${Math.min(value, 100)}%` }} />
  </div>
);

const SitePerformanceTable = ({ siteStats = [], onSiteClick, detailed = false }) => {
  const { t } = useLanguage();
  const getBarColor = (r) => r >= 80 ? 'bg-emerald-500' : r >= 50 ? 'bg-amber-500' : 'bg-red-500';
  const getRateColor = (r) => r >= 80 ? 'text-emerald-600' : r >= 50 ? 'text-amber-600' : 'text-red-600';
  const getStatusBadge = (r, overdue) => {
    if (overdue > 0) return { label: t('needsAttention'), cls: 'bg-orange-100 text-orange-700' };
    if (r >= 80) return { label: t('onTrack'), cls: 'bg-emerald-100 text-emerald-700' };
    if (r >= 50) return { label: t('atRisk'), cls: 'bg-amber-100 text-amber-700' };
    return { label: t('critical'), cls: 'bg-red-100 text-red-700' };
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <h3 className="font-semibold text-foreground font-heading">{t('sitePerformance')}</h3>
        <p className="text-xs text-muted-foreground mt-0.5">{t('clickSiteToViewTasks')}</p>
      </div>
      <div className="divide-y divide-border">
        {siteStats?.map(site => {
          const badge = getStatusBadge(site?.completion_rate, site?.tasks_overdue);
          return (
            <div key={site?.id} onClick={() => onSiteClick?.(site?.id)} className="px-5 py-4 hover:bg-muted/30 cursor-pointer transition-colors">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="Building2" size={18} className="text-accent" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{site?.site_name}</p>
                      {site?.location && <p className="text-xs text-muted-foreground">{site?.location}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${badge?.cls}`}>{badge?.label}</span>
                      <span className={`text-sm font-bold ${getRateColor(site?.completion_rate)}`}>{site?.completion_rate}%</span>
                    </div>
                  </div>
                  <ProgressBar value={site?.completion_rate} color={getBarColor(site?.completion_rate)} />
                  {detailed && (
                    <div className="grid grid-cols-3 gap-3 mt-3">
                      <div className="text-center">
                        <p className="text-base font-bold text-foreground">{site?.tasks_total}</p>
                        <p className="text-xs text-muted-foreground">{t('total')}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-base font-bold text-emerald-600">{site?.tasks_completed}</p>
                        <p className="text-xs text-muted-foreground">{t('done')}</p>
                      </div>
                      <div className="text-center">
                        <p className={`text-base font-bold ${site?.tasks_overdue > 0 ? 'text-orange-600' : 'text-foreground'}`}>{site?.tasks_overdue}</p>
                        <p className="text-xs text-muted-foreground">{t('overdue')}</p>
                      </div>
                    </div>
                  )}
                  {!detailed && (
                    <div className="flex items-center gap-4 mt-1.5">
                      <span className="text-xs text-muted-foreground">{site?.tasks_completed}/{site?.tasks_total} {t('tasks')}</span>
                      {site?.tasks_overdue > 0 && <span className="text-xs text-orange-600 font-semibold flex items-center gap-0.5"><Icon name="AlertCircle" size={11} />{site?.tasks_overdue} {t('overdue')}</span>}
                    </div>
                  )}
                </div>
                <Icon name="ChevronRight" size={16} className="text-muted-foreground flex-shrink-0 mt-1" />
              </div>
            </div>
          );
        })}
        {siteStats?.length === 0 && (
          <div className="p-8 text-center"><p className="text-sm text-muted-foreground">{t('noSiteDataAvailable')}</p></div>
        )}
      </div>
    </div>
  );
};

export default SitePerformanceTable;
